import axiosInstance from '../lib/axios';

// API Response Types
export interface TimeTrackingRecord {
  _id: string;
  employee: string;
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  clockInReason: string | null;
  clockOutReason: string | null;
  isActive: boolean;
  onBreak: boolean;
  breaks: BreakRecord[];
  totalDurationMs: number;
  productiveDurationMs: number;
  breakDurationMs: number;
}

export interface BreakRecord {
  breakInAt: string;
  breakOutAt: string | null;
  reason: string | null;
  durationMs: number;
}

export interface ClockInRequest {
  reason?: string;
}

export interface BreakOutRequest {
  reason?: string;
}

export interface ClockOutRequest {
  reason?: string;
}

export interface DailySummaryDay {
  date: string;
  totalDurationMs: number;
  productiveDurationMs: number;
  breakDurationMs: number;
  clockInAt: string | null;
  clockOutAt: string | null;
}

export interface DailySummaryResponse {
  rangeStart: string;
  rangeEnd: string;
  days: DailySummaryDay[];
  totalDurationMs: number;
  productiveDurationMs: number;
  breakDurationMs: number;
}

export interface MissingClockOutResponse {
  hasMissingClockOut: boolean;
  missingClockOutDate?: string;
  clockInAt?: string;
}

export interface CorrectMissingClockOutRequest {
  clockOutTime: string; // ISO format
  reason: string;
}

class TimeTrackingApiService {
  private baseUrl = '/time-tracking';

  // Clock In
  async clockIn(reason?: string): Promise<TimeTrackingRecord> {
    try {
      const payload: ClockInRequest = {};
      if (reason) {
        payload.reason = reason;
      }
      const response = await axiosInstance.post<TimeTrackingRecord>(
        `${this.baseUrl}/clock-in`,
        Object.keys(payload).length > 0 ? payload : undefined
      );
      return response.data;
    } catch (error: any) {
      console.error('Error clocking in:', error);
      throw new Error(error.response?.data?.message || 'Failed to clock in');
    }
  }

  // Break In
  async breakIn(): Promise<TimeTrackingRecord> {
    try {
      const response = await axiosInstance.post<TimeTrackingRecord>(
        `${this.baseUrl}/break-in`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error starting break:', error);
      throw new Error(error.response?.data?.message || 'Failed to start break');
    }
  }

  // Break Out
  async breakOut(reason?: string): Promise<TimeTrackingRecord> {
    try {
      const payload: BreakOutRequest = {};
      if (reason) {
        payload.reason = reason;
      }
      const response = await axiosInstance.post<TimeTrackingRecord>(
        `${this.baseUrl}/break-out`,
        Object.keys(payload).length > 0 ? payload : undefined
      );
      return response.data;
    } catch (error: any) {
      console.error('Error ending break:', error);
      throw new Error(error.response?.data?.message || 'Failed to end break');
    }
  }

  // Clock Out
  async clockOut(reason?: string): Promise<TimeTrackingRecord> {
    try {
      const payload: ClockOutRequest = {};
      if (reason) {
        payload.reason = reason;
      }
      const response = await axiosInstance.post<TimeTrackingRecord>(
        `${this.baseUrl}/clock-out`,
        Object.keys(payload).length > 0 ? payload : undefined
      );
      return response.data;
    } catch (error: any) {
      console.error('Error clocking out:', error);
      throw new Error(error.response?.data?.message || 'Failed to clock out');
    }
  }

  // Get current active session for the logged-in user
  async getCurrentSession(): Promise<TimeTrackingRecord | null> {
    try {
      // Try the dedicated endpoint first - this is the proper way to get current session with break state
      const response = await axiosInstance.get<TimeTrackingRecord>(
        `${this.baseUrl}/me/current`
      );
      return response.data;
    } catch (error: any) {
      // If endpoint doesn't exist (404) or other error, log it but don't use fallback
      // The fallback loses break state, so it's better to return null and let the frontend handle it
      if (error.response?.status === 404) {
        console.warn('GET /time-tracking/me/current endpoint not found. Break state cannot be restored without this endpoint.');
        console.warn('Please implement GET /time-tracking/me/current endpoint to return current session with break state.');
        return null;
      }
      console.error('Error fetching current session:', error);
      return null;
    }
  }

  // Get Daily Summary (range)
  async getDailySummary(startDate: string, endDate: string): Promise<DailySummaryResponse> {
    try {
      const response = await axiosInstance.get<DailySummaryResponse>(
        `${this.baseUrl}/me/range`,
        {
          params: {
            startDate,
            endDate,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching daily summary:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch daily summary');
    }
  }

  // Helper: Convert milliseconds to TimeTracking format
  msToTimeTracking(ms: number): { hours: number; minutes: number; seconds: number } {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  }

  // Check for missing clock-out
  async checkMissingClockOut(): Promise<MissingClockOutResponse> {
    try {
      const response = await axiosInstance.get<MissingClockOutResponse>(
        `${this.baseUrl}/me/missing-clock-out`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error checking missing clock-out:', error);
      // If endpoint doesn't exist, return no missing clock-out
      if (error.response?.status === 404) {
        return { hasMissingClockOut: false };
      }
      throw new Error(error.response?.data?.message || 'Failed to check missing clock-out');
    }
  }

  // Correct missing clock-out
  async correctMissingClockOut(data: CorrectMissingClockOutRequest): Promise<TimeTrackingRecord> {
    try {
      const response = await axiosInstance.post<TimeTrackingRecord>(
        `${this.baseUrl}/me/correct-missing-clock-out`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error('Error correcting missing clock-out:', error);
      throw new Error(error.response?.data?.message || 'Failed to correct missing clock-out');
    }
  }

  // Helper: Get current week range (last Saturday to today)
  getCurrentWeekRange(): { startDate: string; endDate: string } {
    // Use local date to avoid timezone issues
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Create date objects in local timezone (no time component)
    const todayLocal = new Date(year, month, day);
    const dayOfWeek = todayLocal.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Find last Saturday (inclusive)
    let daysToSubtract = 0;
    if (dayOfWeek === 6) {
      // Today is Saturday, use today
      daysToSubtract = 0;
    } else if (dayOfWeek === 0) {
      // Today is Sunday, go back 1 day to Saturday
      daysToSubtract = 1;
    } else {
      // Monday (1) through Friday (5), go back to Saturday
      // Monday: 1 + 1 = 2 days back
      // Tuesday: 2 + 1 = 3 days back
      // Wednesday: 3 + 1 = 4 days back
      // Thursday: 4 + 1 = 5 days back
      // Friday: 5 + 1 = 6 days back
      daysToSubtract = dayOfWeek + 1;
    }
    
    const lastSaturday = new Date(year, month, day - daysToSubtract);
    
    // Format dates as YYYY-MM-DD in local timezone
    const formatDate = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const startDate = formatDate(lastSaturday);
    const endDate = formatDate(todayLocal);
    
    return { startDate, endDate };
  }
}

export const timeTrackingApiService = new TimeTrackingApiService();

